import { watch, readdirSync } from 'fs'
import { EventEmitter } from 'events'
import { join, relative } from 'path'
import { existsSync } from 'fs'

export type WatchChangeType = 'added' | 'modified' | 'deleted'

export interface FileChange {
  path: string
  type: WatchChangeType
}

export interface FileChangeEvent {
  changes: FileChange[]
  timestamp: number
}

export interface FileWatcherOptions {
  projectPath: string
  debounceMs?: number
}

/**
 * File watcher for ADK agent projects
 *
 * Watches the following patterns:
 * - src/**\/*
 * - dependencies.json
 * - package.json
 * - agent.json
 * - agent.config.ts
 */
export class FileWatcher extends EventEmitter {
  private projectPath: string
  private watchers: Map<string, ReturnType<typeof watch>> = new Map()
  private fileStates: Map<string, number> = new Map()
  private debounceMs: number
  private debounceTimer: NodeJS.Timeout | null = null
  private pendingChanges: Map<string, FileChange> = new Map()

  constructor(options: FileWatcherOptions) {
    super()
    this.projectPath = options.projectPath
    this.debounceMs = options.debounceMs ?? 100
  }

  /**
   * Start watching files
   */
  start(): void {
    // Watch individual files at project root
    const rootFiles = ['dependencies.json', 'package.json', 'agent.json', 'agent.config.ts']

    for (const file of rootFiles) {
      const filePath = join(this.projectPath, file)
      if (existsSync(filePath)) {
        this.watchFile(filePath)
      }
    }

    // Watch src directory recursively
    const srcPath = join(this.projectPath, 'src')
    if (existsSync(srcPath)) {
      // Initialize state for all existing files BEFORE setting up watchers
      this.initializeDirectoryState(srcPath)
      // Now set up the watcher
      this.watchDirectory(srcPath)
    }
  }

  /**
   * Initialize state for all files in a directory (recursive)
   */
  private initializeDirectoryState(dirPath: string): void {
    try {
      const entries = readdirSync(dirPath, { withFileTypes: true })

      for (const entry of entries) {
        const fullPath = join(dirPath, entry.name)
        if (entry.isDirectory()) {
          this.initializeDirectoryState(fullPath)
        } else if (entry.isFile()) {
          this.updateFileState(fullPath)
        }
      }
    } catch (error) {
      // Ignore errors during initialization
    }
  }

  /**
   * Watch a single file
   */
  private watchFile(filePath: string): void {
    if (this.watchers.has(filePath)) {
      return
    }

    try {
      // Initialize file state BEFORE setting up watcher to avoid triggering events
      this.updateFileState(filePath)

      const watcher = watch(filePath, { persistent: true }, () => {
        this.handleFileChange(filePath)
      })

      this.watchers.set(filePath, watcher)
    } catch (error) {
      console.warn(`Failed to watch file ${filePath}:`, error)
    }
  }

  /**
   * Watch a directory recursively
   */
  private watchDirectory(dirPath: string): void {
    if (this.watchers.has(dirPath)) {
      return
    }

    try {
      const watcher = watch(dirPath, { recursive: true, persistent: true }, (_eventType, filename) => {
        if (filename) {
          const fullPath = join(dirPath, filename)
          this.handleFileChange(fullPath)
        }
      })

      this.watchers.set(dirPath, watcher)
    } catch (error) {
      console.warn(`Failed to watch directory ${dirPath}:`, error)
    }
  }

  /**
   * Handle file change events - accumulates changes for batch emission
   */
  private handleFileChange(filePath: string): void {
    const fileExists = existsSync(filePath)
    const previousState = this.fileStates.get(filePath)

    let changeType: WatchChangeType

    if (!fileExists && previousState !== undefined) {
      // File was deleted
      changeType = 'deleted'
      this.fileStates.delete(filePath)
    } else if (fileExists && previousState === undefined) {
      // File was added
      changeType = 'added'
      this.updateFileState(filePath)
    } else if (fileExists) {
      // File was modified
      changeType = 'modified'
      this.updateFileState(filePath)
    } else {
      // Unknown state, skip
      return
    }

    const relativePath = relative(this.projectPath, filePath)

    // Add to pending changes (overwrites previous change for same file)
    this.pendingChanges.set(relativePath, {
      path: relativePath,
      type: changeType,
    })

    // Reset debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.emitPendingChanges()
    }, this.debounceMs)
  }

  /**
   * Emit all pending changes as a single event
   */
  private emitPendingChanges(): void {
    if (this.pendingChanges.size === 0) {
      return
    }

    const changes = Array.from(this.pendingChanges.values())
    this.pendingChanges.clear()

    const event: FileChangeEvent = {
      changes,
      timestamp: Date.now(),
    }

    this.emit('change', event)
  }

  /**
   * Update internal file state tracking
   */
  private updateFileState(filePath: string): void {
    if (existsSync(filePath)) {
      this.fileStates.set(filePath, Date.now())
    }
  }

  /**
   * Stop watching all files
   */
  stop(): void {
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    // Close all watchers
    for (const watcher of this.watchers.values()) {
      watcher.close()
    }
    this.watchers.clear()
    this.fileStates.clear()
    this.pendingChanges.clear()
  }

  /**
   * Type-safe event listener
   */
  on(event: 'change', listener: (event: FileChangeEvent) => void): this {
    return super.on(event, listener)
  }

  /**
   * Type-safe event emitter
   */
  emit(event: 'change', data: FileChangeEvent): boolean {
    return super.emit(event, data)
  }
}
